import queryString from 'query-string';
import scriptjs from 'scriptjs';
import {autobind} from 'core-decorators';
import {getDOMNode, getDOMNodes} from 'utils';
import BaseAdapter from './base';

export const loadScript = scriptjs;

// https://developers.google.com/youtube/iframe_api_reference

@autobind
export default class YouTubeAdapter extends BaseAdapter {
    apiUrls = {
        data: 'https://www.googleapis.com/youtube/v3/videos?',
        player: 'https://www.youtube.com/iframe_api',
    }

    static findVideos() {
        const nodes = getDOMNodes('iframe[src*="youtube.com/embed/"]');
        return nodes.map(iframe => ({
            provider: 'youtube',
            videoId: iframe.src.match(/embed\/([\w-]+)/)[1],
            videoNode: iframe,
        }));
    }

    loadAPI() {
        return new Promise((resolve, reject) => {
            if (window.YT && window.YT.loaded) return resolve();

            const previousCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (previousCallback) previousCallback();
                resolve();
            };

            if (getDOMNode(`script[src="${this.apiUrls.player}"]`) === null) {
                exports.loadScript(this.apiUrls.player);
            }

            const timeout = () => reject('YouTube IFrame API failed to load');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    attach() {
        return new Promise((resolve, reject) => {
            this.enforcePlayerParameters();
            this.api = new window.YT.Player(this.videoNode);
            this.api.addEventListener('onReady', this.handleReady);
            this.api.addEventListener('onStateChange', this.handleStateChange);
            this.api.addEventListener('onReady', () => resolve());
            this.api.addEventListener('onError', () => reject('YouTube Player initialization failed'));
        });
    }

    enforcePlayerParameters() {
        // https://developers.google.com/youtube/player_parameters
        const src = this.videoNode.src.split('?');
        const base = src[0];
        const qs = queryString.parse(src[1]);

        if (!qs.enablejsapi || !qs.playsinline) {
            qs.enablejsapi = 1;
            qs.playsinline = 1;
            this.videoNode.src = `${base}?${queryString.stringify(qs)}`;
        }
    }

    handleReady() {
        this.prevCurrentTime = 0;
        this.prevSyncTimestamp = new Date().getTime();
        this.setDuration(this.getDuration());
    }

    handleStateChange() {
        const stateNames = {
            [window.YT.PlayerState.BUFFERING]: BaseAdapter.STATES.Buffering,
            [window.YT.PlayerState.PAUSED]: BaseAdapter.STATES.Paused,
            [window.YT.PlayerState.PLAYING]: BaseAdapter.STATES.Playing,
            [window.YT.PlayerState.ENDED]: BaseAdapter.STATES.Ended,
        };

        const state = stateNames[this.api.getPlayerState()];
        if (state) this.setPlayerState(state);
        if (state === BaseAdapter.STATES.Playing) this.seeking = false;

        if (this.isPlaying()) this.syncPlayhead();
    }

    syncPlayhead() {
        const syncInterval = 200;
        const seekTolerance = 500;
        const currentTime = this.getCurrentTime();
        const syncTimestamp = new Date().getTime();
        const timeDelta = Math.abs(currentTime - this.prevCurrentTime);
        const syncTimestampDelta = Math.abs(syncTimestamp - this.prevSyncTimestamp);

        if (timeDelta > syncTimestampDelta + seekTolerance) {
            this.seeking = false;
            this.setPlayerState(BaseAdapter.STATES.Seek);
        }

        this.prevCurrentTime = currentTime;
        this.prevSyncTimestamp = syncTimestamp;

        if (!this.seeking) this.setPlayhead(currentTime);
        if (this.isPlaying()) setTimeout(this.syncPlayhead, syncInterval);
    }

    seek(time) {
        this.seeking = true;
        this.api.seekTo(this.toSS(time), true);
    }

    pause() {
        if (this.isPlaying()) this.api.pauseVideo();
    }

    isPlaying() {
        return this.api.getPlayerState() === window.YT.PlayerState.PLAYING;
    }

    getDuration() {
        return this.toMS(this.api.getDuration());
    }

    getCurrentTime() {
        return this.toMS(this.api.getCurrentTime());
    }

    getVideoTitle() {
        const videoData = this.api.getVideoData();
        return Promise.resolve(videoData.title);
    }

    async getVideoData() {
        const endpoint = `${this.apiUrls.data}part=snippet&id=${this.videoId}&key=${VWRAP.youtubeApiKey}`;

        let videoData;
        try {
            const response = await fetch(endpoint);
            videoData = await response.json();
        } catch (error) {
            return null;
        }

        if (videoData.error) return null; // data requested is not public

        return videoData.items[0].snippet;
    }

    async getMetadata() {
        const videoData = await this.getVideoData();

        if (!videoData) return {};

        const {
            title,
            publishedAt,
            description,
            thumbnails: {default: defaultThumbnail, standard, medium, high, maxres},
        } = videoData;

        const contentUrl = this.api.getVideoUrl();
        const uploadDate = new Date(publishedAt).toISOString();
        const thumbnailUrl = (defaultThumbnail || standard || medium || high || maxres).url;

        return {
            name: title,
            contentUrl,
            uploadDate,
            description,
            thumbnailUrl,
        };
    }
}




// WEBPACK FOOTER //
// ./scripts/adapters/youtube.js